import { ReactNode, useMemo } from 'react';
import { IonIcon } from '@ionic/react';
import {
  arrowForwardCircleOutline,
  checkmarkCircleOutline,
  closeCircleOutline,
} from 'ionicons/icons';
import Button from '../../../../Button/Button';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import { useDatasetFormData } from '../../../../../hooks/context/useDatasetFormData';
import { useFormHandlersContext } from '../../../../../hooks/context/useFormHandlersContext';
import { HandlerTriggerControl } from '../../../../../types/types';
import { useSelectedRowsContext } from '../../../../../hooks/context/useSelectedRowsContext';

interface FormHeaderProps {
  header: string;
  paginator?: ReactNode;
}

const FormHeader = ({ header, paginator }: FormHeaderProps) => {
  const { t } = useTranslate();
  const {
    form,
    submitDatasetForm,
    closeDatasetForm,
    submitAndNext,
    controlSelectionMode,
    multiSelectMode,
    currentSelectedRowIndex,
    formValuesChanged,
    forceSave,
    formType,
    isSubmitting,
  } = useDatasetFormData();
  const { runHeaderHandler } = useFormHandlersContext();
  const handlerTriggerControls = (form?.controls ?? []).filter(
    (c): c is HandlerTriggerControl => c.type === 'handlerTrigger'
  );

  const { selectedRows } = useSelectedRowsContext();

  // Show only in sequential multi-select when not on the last row; hidden for 'dontcare' (no rows selected)
  const showSaveAndNext: boolean = useMemo(
    () =>
      controlSelectionMode !== 'dontcare' &&
      multiSelectMode === 'sequential' &&
      selectedRows.length > 1 &&
      currentSelectedRowIndex < selectedRows.length - 1,
    [
      controlSelectionMode,
      multiSelectMode,
      selectedRows.length,
      currentSelectedRowIndex,
    ]
  );

  const disableSubmitButton: boolean = useMemo(
    () =>
      ((formType === 'edit' || formType === 'other') &&
        !formValuesChanged &&
        !forceSave) ||
      isSubmitting,
    [formValuesChanged, formType, forceSave, isSubmitting]
  );

  return (
    <div className="header">
      <h1>{header}</h1>
      <Button
        onClick={() => submitDatasetForm()}
        fill="clear"
        color="success"
        disabled={disableSubmitButton}
      >
        <IonIcon icon={checkmarkCircleOutline} slot="start" />
        {t(form?.submitLabel ?? 'save')}
      </Button>
      {showSaveAndNext && (
        <Button
          onClick={submitAndNext}
          fill="clear"
          color="success"
          disabled={disableSubmitButton}
        >
          <IonIcon icon={arrowForwardCircleOutline} slot="start" />
          {t('saveAndNext')}
        </Button>
      )}
      <Button
        onClick={() => closeDatasetForm()}
        fill="clear"
        color="danger"
        disabled={isSubmitting}
      >
        <IonIcon icon={closeCircleOutline} slot="start" />
        {t('cancel')}
      </Button>
      {handlerTriggerControls.map((control) => (
        <Button
          key={`${control.method}-${control.label}`}
          onClick={() => runHeaderHandler(control.method, control.triggerData)}
          fill="clear"
          color="secondary"
          title={control.hint}
          disabled={isSubmitting}
        >
          {control.label}
        </Button>
      ))}
      {paginator}
    </div>
  );
};

export default FormHeader;
